import React, { useState } from "react";
import DateTimePicker from "./DateTimePicker";
import { format } from "date-fns";
import axios from "axios";
import StockInOutModal from "./StockInOutModal";
import MaterialListSelect from "./MaterialListSelect";
import MaterialLocationSelect from "./MaterialLocationSelect";
import { toast } from "react-toastify";
import { formatNumber } from "../utils/numbers";

const VIEW_OPTIONS = [
  { value: "0", label: "Show All" },
  { value: "1", label: "Stock In" },
  { value: "2", label: "Stock Out" },
  { value: "3", label: "Search" },
  { value: "4", label: "Approved Records" },
  { value: "5", label: "Pending Records" },
];

const SEARCH_OPTIONS = [
  { value: "0", label: "By Product Name / Code" },
  { value: "1", label: "By Remark / Operator" },
];

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400";
const thCls =
  "px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-r last:border-r-0";
const tdCls = "px-3 py-2 text-xs border-r last:border-r-0 whitespace-nowrap";

function TransactionRecord() {
  const [selectedMatList, setSelectedMatList] = useState(null);
  const [viewType, setViewType] = useState("0");
  const [searchByType, setSearchByType] = useState("0");
  const [fromDateSelected, setFromDateSelected] = useState(new Date());
  const [toDateSelected, setToDateSelected] = useState(
    new Date(new Date().setDate(new Date().getDate() + 7)),
  );
  const [remark, setRemark] = useState("");
  const [record, setRecord] = useState([]);
  const [searched, setSearched] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [productLocation, setProductLocation] = useState("");

  const handleToDateChange = (date) => {
    if (date < fromDateSelected) {
      toast.warning("'To' date cannot be before 'From' date.");
      return;
    }
    setToDateSelected(date);
  };

  const handleViewTypeChange = (e) => {
    setViewType(e.target.value);
    setRecord([]);
    setRemark("");
    setSearched(false);
  };

  const handleSearchTypeChange = (e) => {
    setSearchByType(e.target.value);
    setRecord([]);
  };

  const isVoid = (data) => data.transaction_remark?.includes("[VOID]");

  const typeColor = (type) => {
    if (type === "Stock In") return "bg-green-100 text-green-700";
    if (type === "Stock Out") return "bg-red-100 text-red-700";
    if (
      [
        "Pending Stock In",
        "QC Pending Stock In",
        "Pending",
        "Stock Out Pending",
      ].includes(type)
    )
      return "bg-yellow-100 text-yellow-700";
    if (type === "CANCELLED") return "bg-red-200 text-red-700";
    return "bg-gray-100 text-gray-600";
  };

  const fmtDate = (iso) => {
    try {
      return format(new Date(iso), "dd-MM-yyyy HH:mm");
    } catch {
      return "—";
    }
  };

  const fetch = async (fn) => {
    try {
      await fn();
      setSearched(true);
    } catch {
      toast.error("Failed to fetch transaction records.");
    }
  };

  const getAllTransaction = () =>
    fetch(async () => {
      const r = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/transaction/?from=${format(fromDateSelected, "yyyy-MM-dd")}&to=${format(toDateSelected, "yyyy-MM-dd")}`,
      );
      setRecord(r.data.result);
    });
  const getAllApproved = () =>
    fetch(async () => {
      const r = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/transaction/approved/?from=${format(fromDateSelected, "yyyy-MM-dd")}&to=${format(toDateSelected, "yyyy-MM-dd")}`,
      );
      setRecord(r.data.result);
    });
  const getAllPending = () =>
    fetch(async () => {
      const r = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/transaction/pending/`,
      );
      setRecord(r.data.result);
    });
  const getByType = (type) =>
    fetch(async () => {
      const r = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/transaction/type?from=${format(fromDateSelected, "yyyy-MM-dd")}&to=${format(toDateSelected, "yyyy-MM-dd")}&types=${encodeURIComponent(type)}`,
      );
      setRecord(r.data.result);
    });
  const searchById = (id) =>
    fetch(async () => {
      const r = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/transaction/${id}`,
      );
      setRecord(r.data.result);
    });
  const searchByRemark = (q) =>
    fetch(async () => {
      const r = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/transaction/search?search=${q}`,
      );
      setRecord(r.data.result);
    });

  const refreshData = () => {
    switch (viewType) {
      case "0":
        getAllTransaction();
        break;
      case "1":
        getByType("Stock In");
        break;
      case "2":
        getByType("Stock Out");
        break;
      case "3":
        searchByType === "0"
          ? searchById(selectedMatList)
          : searchByRemark(remark);
        break;
      case "4":
        getAllApproved();
        break;
      case "5":
        getAllPending();
        break;
      default:
        getAllTransaction();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (viewType === "3" && searchByType === "1" && !remark) {
      toast.warning("Please enter a remark / operator to search.");
      return;
    }
    refreshData();
  };

  const showDatePicker = viewType !== "3" && viewType !== "5";

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {/* Header */}
      <div className="w-full rounded-lg bg-slate-700 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Transaction Records
        </label>
      </div>

      {/* Body card */}
      <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Controls */}
        <form
          onSubmit={handleSubmit}
          className="flex-shrink-0 px-6 py-5 border-b border-gray-100 space-y-4"
        >
          {/* View type */}
          <div>
            <label className={lbl}>View Mode</label>
            <select
              className={inp}
              value={viewType}
              onChange={handleViewTypeChange}
            >
              {VIEW_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Search sub-options */}
          {viewType === "3" && (
            <div className="space-y-3">
              <div>
                <label className={lbl}>Search By</label>
                <select
                  className={inp}
                  value={searchByType}
                  onChange={handleSearchTypeChange}
                >
                  {SEARCH_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              {searchByType === "0" ? (
                <div className="space-y-3">
                  <MaterialLocationSelect onChangeList={setProductLocation} />
                  <MaterialListSelect
                    onChangeList={(Arr) => setSelectedMatList(Arr[0])}
                    location_id={productLocation}
                  />
                </div>
              ) : (
                <div>
                  <label className={lbl}>Remark / Operator</label>
                  <input
                    className={inp}
                    type="text"
                    placeholder="Enter remark or operator name"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value.toUpperCase())}
                  />
                </div>
              )}
            </div>
          )}

          {/* Date pickers */}
          {showDatePicker && (
            <DateTimePicker
              firstLabel="From"
              secondLabel="To"
              firstDateValue={fromDateSelected}
              secondDateValue={toDateSelected}
              onChangeFirstDate={setFromDateSelected}
              onChangeSecondDate={handleToDateChange}
            />
          )}

          <button
            type="submit"
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-slate-700 hover:bg-slate-800 transition"
          >
            Check Records
          </button>
        </form>

        {/* Table — fixed header + scrollable body */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {!searched ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 italic text-sm">
              Select a view mode and click Check Records
            </div>
          ) : record.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 italic text-sm">
              No records found
            </div>
          ) : (
            <>
              {/* Fixed header — never scrolls */}
              <table className="w-full text-xs table-fixed flex-shrink-0">
                <colgroup>
                  <col style={{ width: "44px" }} />
                  <col />
                  <col style={{ width: "90px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "65px" }} />
                  <col style={{ width: "65px" }} />
                  <col style={{ width: "130px" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "110px" }} />
                  <col style={{ width: "110px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "110px" }} />
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "70px" }} />
                </colgroup>
                <thead className="bg-gray-700 text-white uppercase">
                  <tr>
                    <th className={thCls}>No</th>
                    <th className={thCls}>Product Name</th>
                    <th className={thCls}>Code</th>
                    <th className={thCls}>Group</th>
                    <th className={thCls}>Stock In</th>
                    <th className={thCls}>Stock Out</th>
                    <th className={thCls}>Type</th>
                    <th className={thCls}>Customer</th>
                    <th className={thCls}>Remark</th>
                    <th className={thCls}>Input By</th>
                    <th className={thCls}>Input Time</th>
                    <th className={thCls}>Modified</th>
                    <th className={thCls}>Group ID</th>
                    <th className={thCls}>Approved Time</th>
                    <th className={thCls}>Approved By</th>
                    <th className={thCls}>Form No</th>
                  </tr>
                </thead>
              </table>
              {/* Scrollable body */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                <table
                  id="transactionTable"
                  className="w-full text-xs border-collapse table-fixed"
                >
                  <colgroup>
                    <col style={{ width: "44px" }} />
                    <col />
                    <col style={{ width: "90px" }} />
                    <col style={{ width: "80px" }} />
                    <col style={{ width: "65px" }} />
                    <col style={{ width: "65px" }} />
                    <col style={{ width: "130px" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "80px" }} />
                    <col style={{ width: "110px" }} />
                    <col style={{ width: "110px" }} />
                    <col style={{ width: "80px" }} />
                    <col style={{ width: "110px" }} />
                    <col style={{ width: "80px" }} />
                    <col style={{ width: "70px" }} />
                  </colgroup>
                  <tbody>
                    {record.map((data, i) => (
                      <tr
                        key={`ts-${i}`}
                        onDoubleClick={() => {
                          setModalData(data);
                          setShowModal(true);
                        }}
                        className={`border-b cursor-pointer hover:bg-blue-50 ${
                          isVoid(data)
                            ? "bg-red-100 text-red-400 line-through"
                            : i % 2 === 0
                              ? "bg-gray-50"
                              : "bg-white"
                        }`}
                      >
                        <td className={`${tdCls} text-gray-400`}>{i + 1}</td>
                        <td className={`${tdCls} font-medium text-gray-800 truncate`} title={data.product_name}>
                          {data.product_name}
                        </td>
                        <td className={`${tdCls} truncate`} title={data.product_code}>{data.product_code}</td>
                        <td className={`${tdCls} truncate`}>{data.group_name}</td>
                        <td className={`${tdCls} font-bold text-green-700`}>
                          {formatNumber(data.stock_in)}
                        </td>
                        <td className={`${tdCls} font-bold text-red-700`}>
                          {formatNumber(data.stock_out)}
                        </td>
                        <td className="px-3 py-2 border-r">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${typeColor(data.transaction_type)}`}
                          >
                            {data.transaction_type}
                          </span>
                        </td>
                        <td className={`${tdCls} truncate`} title={data.transaction_for}>{data.transaction_for || "—"}</td>
                        <td className={`${tdCls} truncate`} title={data.transaction_remark}>
                          {data.transaction_remark || "—"}
                        </td>
                        <td className={`${tdCls} truncate`}>{data.transaction_by}</td>
                        <td className={tdCls}>{fmtDate(data.transaction_time)}</td>
                        <td className={tdCls}>{fmtDate(data.transaction_modified)}</td>
                        <td className={tdCls}>
                          {data.transaction_group_id ? (
                            <span
                              className="font-mono text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded"
                              title={data.transaction_group_id}
                            >
                              {data.transaction_group_id.slice(0, 8)}…
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className={tdCls}>
                          {data.approved_time ? fmtDate(data.approved_time) : "—"}
                        </td>
                        <td className={`${tdCls} truncate`}>{data.approved_by || "—"}</td>
                        <td className={tdCls}>{data.form_id || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer count */}
        {searched && record.length > 0 && (
          <div className="flex-shrink-0 px-5 py-2.5 border-t border-gray-100 bg-gray-50">
            <span className="text-xs text-gray-400">
              {record.length} record{record.length !== 1 ? "s" : ""} —
              double-click a row to edit
            </span>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <StockInOutModal
            dataSet={modalData}
            onClose={() => setShowModal(false)}
            refreshData={refreshData}
          />
        </div>
      )}
    </div>
  );
}

export default TransactionRecord;
